import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../Experience'
import EventEmitter from '../../Utils/EventEmitter'
import CelestialBody from './CelestialBody'
import MoonRays from './Effects/MoonRays'
import GodRayHalo from './Effects/GodRayHalo'
import AuroraCurtains from './Effects/AuroraCurtains'
import MeteorShower from './Effects/MeteorShower'
import RingBurst from './Effects/RingBurst'
import { CELESTIAL_BODIES, TRIGGER_ZONE, HALO_ZONE_SCALE } from './celestialConfig'

/**
 * OrbitingCelestials — 周回する天体たちのマネージャ。
 *
 * - config から天体を生成（low tier では minTier: 'high' の天体をスキップ）
 * - StarrySky と同じく 7 秒後に公転開始、reducedMotion 中は時間を進めない
 * - update() は World.update() からのみ呼ぶこと（time.on('tick') と併用しない。
 *   StarrySky の二重 update の教訓と同じ）
 *
 * トリガーゾーン: ワールド空間のコーン（celestialConfig.TRIGGER_ZONE）。
 * 天体がコーンに入ると 'bodyEnterZone'、出ると 'bodyExitZone' を発火し、
 * 対応するエフェクトを GSAP でフェードイン/アウトさせる。
 * enter角 < exit角 のヒステリシスで境界のチラつきを防ぐ。
 */
export default class OrbitingCelestials extends EventEmitter
{
  constructor()
  {
    super()

    this.experience = new Experience()
    this.scene = this.experience.scene
    this.time = this.experience.time
    this.camera = this.experience.camera
    this.quality = this.experience.quality
    this.debug = this.experience.debug

    this.isOrbiting = false
    this.orbitTime = 0        // 公転専用の累積時間（秒）。isOrbiting 中だけ進む
    this.speedMultiplier = 1  // デバッグ用の早送り倍率

    this.triggerDir = new THREE.Vector3(...TRIGGER_ZONE.direction).normalize()
    this.enterDot = Math.cos(TRIGGER_ZONE.enterAngle)
    this.exitDot = Math.cos(TRIGGER_ZONE.exitAngle)
    this.scratchVec3 = new THREE.Vector3()

    this.setBodies()
    this.setDebug()

    // 暗いイントロの間にゆっくり浮かび上がらせる
    gsap.delayedCall(0.5, () => this.fadeInBodies())

    // 星空の回転開始（StarrySky の delayedCall(7.0)）と同期
    gsap.delayedCall(7.0, () => {
      this.isOrbiting = true
    })
  }

  setBodies()
  {
    this.bodies = []

    for (const config of CELESTIAL_BODIES) {
      if (this.quality.tier === 'low' && config.minTier === 'high') continue

      const body = new CelestialBody(config)
      body.effectModule = this.createEffect(config)
      // エフェクトが天体本体（リング増光など）へアクセスできるように参照を渡す
      if (body.effectModule) {
        body.effectModule.body = body
      }
      this.bodies.push(body)
    }

    // 初期位相を反映しておく（公転開始前から正しい位置に見せる）
    for (const body of this.bodies) {
      body.setAngle(body.config.phase)
    }

    /* reducedMotion 時は公転しないので、エフェクト持ちの先頭の天体を
       ゾーン内に駐留させて、演出が一つは見られるようにする */
    if (this.quality.reducedMotion) {
      const parked = this.bodies.find((body) => body.effectModule)
      if (parked) {
        parked.config = { ...parked.config, phase: parked.config.zoneTheta }
        parked.setAngle(parked.config.phase)
      }
    }
  }

  // config.effect 名 → エフェクトモジュール。未知のものは null（発動なし）
  createEffect(config)
  {
    switch (config.effect) {
      case 'moonRays': return new MoonRays(config)
      case 'godRays': return new GodRayHalo(config)
      case 'aurora': return new AuroraCurtains(config)
      case 'meteorShower': return new MeteorShower(config)
      case 'ringBurst': return new RingBurst(config)
      default: return null
    }
  }

  fadeInBodies()
  {
    for (const body of this.bodies) {
      gsap.to(body.bodyMaterial.uniforms.uAlpha, {
        value: 1.0,
        duration: 4.0 * this.quality.motionScale || 0.01,
        ease: 'power2.in',
      })
      gsap.to(body.haloMaterial.uniforms.uIntensity, {
        value: 1.0,
        duration: 5.0 * this.quality.motionScale || 0.01,
        ease: 'power2.in',
      })
      if (body.ambientMaterial) {
        gsap.to(body.ambientMaterial.uniforms.uAlpha, {
          value: 1.0,
          duration: 5.0 * this.quality.motionScale || 0.01,
          ease: 'power2.in',
        })
      }
      if (body.ringMaterial) {
        gsap.to(body.ringMaterial.uniforms.uAlpha, {
          value: 1.0,
          duration: 4.0 * this.quality.motionScale || 0.01,
          ease: 'power2.in',
        })
      }
    }
  }

  setDebug()
  {
    if (!this.debug.active) return

    this.debugFolder = this.debug.ui.addFolder('Orbiting Celestials')
    this.debugFolder.add(this, 'isOrbiting').name('Orbiting').listen()
    this.debugFolder.add(this, 'speedMultiplier')
      .min(0).max(10).step(0.1).name('Speed Multiplier')

    // トリガーゾーンの調整
    const zoneParams = {
      enterAngleDeg: (TRIGGER_ZONE.enterAngle * 180) / Math.PI,
      exitAngleDeg: (TRIGGER_ZONE.exitAngle * 180) / Math.PI,
      showZoneHelper: false,
    }
    this.debugFolder.add(zoneParams, 'enterAngleDeg')
      .min(5).max(60).step(1).name('Enter Angle (deg)')
      .onChange((value) => { this.enterDot = Math.cos((value * Math.PI) / 180) })
    this.debugFolder.add(zoneParams, 'exitAngleDeg')
      .min(5).max(70).step(1).name('Exit Angle (deg)')
      .onChange((value) => { this.exitDot = Math.cos((value * Math.PI) / 180) })
    this.debugFolder.add(zoneParams, 'showZoneHelper')
      .name('Show Zone Helper')
      .onChange((visible) => this.toggleZoneHelper(visible))

    for (const body of this.bodies) {
      const folder = this.debugFolder.addFolder(body.name)
      folder.add(body, 'inZone').name('In Zone').listen()
      folder.add({ fadeIn: () => this.activateEffect(body) }, 'fadeIn').name('▶ Force Effect In')
      folder.add({ fadeOut: () => this.deactivateEffect(body) }, 'fadeOut').name('⏹ Force Effect Out')
      folder.add(body.haloMesh.scale, 'x')
        .min(0).max(15).step(0.1).name('Halo Scale')
        .onChange((value) => body.haloMesh.scale.setScalar(value))
      folder.addColor(body.haloMaterial.uniforms.uColor, 'value').name('Halo Color')
      folder.addColor(body.bodyMaterial.uniforms.uColorA, 'value').name('Color A')
      folder.addColor(body.bodyMaterial.uniforms.uColorB, 'value').name('Color B')
      folder.add(body.bodyMaterial.uniforms.uBandFreq, 'value')
        .min(1).max(30).step(0.5).name('Band Freq / Detail')

      // ダイアゴナルストリーク持ちのエフェクトは専用スライダーを追加
      if (body.effectModule?.streakMaterial) {
        const streakUniforms = body.effectModule.streakMaterial.uniforms
        folder.add(streakUniforms.uRayCount, 'value')
          .min(4).max(60).step(1).name('Streak Count')
        folder.add(streakUniforms.uSharpness, 'value')
          .min(0.5).max(8).step(0.1).name('Streak Sharpness')
      }

      // レンズフレア持ちのエフェクトは強さスライダーを追加
      if (body.effectModule?.flareMaterial) {
        folder.add(body.effectModule.flareMaterial.uniforms.uStrength, 'value')
          .min(0).max(2).step(0.01).name('Flare Strength')
      }

      // 渦巻き銀河持ちのエフェクトは色と ON/OFF を追加（celestialConfig の galaxy が初期値）
      // 色は THREE.Color を直接渡さず sRGB の hex 文字列を介す
      // （直接渡すとリニア値がそのまま表示・書き込みされ、config の hex とズレてしまう）
      if (body.effectModule?.galaxyMaterial) {
        const galaxyUniforms = body.effectModule.galaxyMaterial.uniforms
        const galaxyColors = {
          inner: `#${galaxyUniforms.uColorInner.value.getHexString()}`,
          outer: `#${galaxyUniforms.uColorOuter.value.getHexString()}`,
        }
        folder.addColor(galaxyColors, 'inner').name('Galaxy Inner Color')
          .onChange((value) => galaxyUniforms.uColorInner.value.set(value))
        folder.addColor(galaxyColors, 'outer').name('Galaxy Outer Color')
          .onChange((value) => galaxyUniforms.uColorOuter.value.set(value))
        folder.add(body.effectModule, 'galaxyEnabled').name('Galaxy Particles')
          .onChange((enabled) => body.effectModule.setGalaxyEnabled(enabled))
      }

      // オーロラのサブエフェクトの ON/OFF（celestialConfig.AURORA_SUB_EFFECTS が初期値）
      if (body.effectModule?.setSceneTintEnabled) {
        folder.add(body.effectModule, 'sceneTintEnabled').name('Scene Tint')
          .onChange((enabled) => body.effectModule.setSceneTintEnabled(enabled))
        folder.add(body.effectModule, 'particlesEnabled').name('Falling Particles')
          .onChange((enabled) => body.effectModule.setParticlesEnabled(enabled))
      }
      folder.close()
    }

    this.debugFolder.close()
  }

  update()
  {
    // reducedMotion 中は公転を進めない（天体は phase の位置で静止）
    if (this.isOrbiting && !this.quality.reducedMotion) {
      this.orbitTime += this.time.delta * 0.001 * this.speedMultiplier
    }

    const cameraQuaternion = this.camera.instance.quaternion

    for (const body of this.bodies) {
      body.setAngle(body.config.phase + body.config.speed * this.orbitTime)
      body.update(this.time.elapsed, cameraQuaternion)

      this.updateTrigger(body)

      if (body.effectModule) {
        body.effectModule.updatePosition(body.getWorldPosition(this.scratchVec3))
        body.effectModule.update(this.time.elapsed, cameraQuaternion)
      }
    }
  }

  // 天体がトリガーゾーン中心にどれだけ近いか（1 = ゾーン内、falloffDot 以遠で 0）。
  // Signalscope のシグナル音量・音程などが利用する
  getZoneProximity(body, falloffDot)
  {
    const dot = body.getWorldPosition(this.scratchVec3).normalize().dot(this.triggerDir)
    return THREE.MathUtils.smoothstep(dot, falloffDot, this.enterDot)
  }

  // ワールド空間コーンの出入りを判定（ヒステリシス付き）
  updateTrigger(body)
  {
    const dot = body.getWorldPosition(this.scratchVec3).normalize().dot(this.triggerDir)

    if (!body.inZone && dot > this.enterDot) {
      body.inZone = true
      this.trigger('bodyEnterZone', [body.name])
      this.activateEffect(body)
    }
    else if (body.inZone && dot < this.exitDot) {
      body.inZone = false
      this.trigger('bodyExitZone', [body.name])
      this.deactivateEffect(body)
    }
  }

  // エフェクト発動: 固有エフェクトのフェードイン + ハローを最大まで拡大
  activateEffect(body)
  {
    body.expandHalo(HALO_ZONE_SCALE)
    if (body.effectModule) body.effectModule.fadeIn()
  }

  // エフェクト解除: 固有エフェクトのフェードアウト + ハローを元のサイズへ
  deactivateEffect(body)
  {
    body.restoreHalo()
    if (body.effectModule) body.effectModule.fadeOut()
  }

  // デバッグ用: トリガーコーンの向きを可視化する矢印（遅延生成）
  toggleZoneHelper(visible)
  {
    if (!this.zoneHelper && visible) {
      this.zoneHelper = new THREE.ArrowHelper(
        this.triggerDir, new THREE.Vector3(0, 0, 0), 24, 0xff4466, 3, 1.5
      )
      this.scene.add(this.zoneHelper)
    }
    if (this.zoneHelper) {
      this.zoneHelper.visible = visible
    }
  }

  dispose()
  {
    for (const body of this.bodies) {
      if (body.effectModule) body.effectModule.dispose()
      body.dispose()
    }
    this.bodies = []

    if (this.zoneHelper) {
      this.scene.remove(this.zoneHelper)
    }

    if (this.debugFolder) {
      this.debugFolder.destroy()
    }
  }
}
